import {
  CurrencyCircleDollarIcon,
  FilmStripIcon,
  HandshakeIcon,
  MonitorPlayIcon,
} from '@phosphor-icons/react';
import { PermissionEnum, TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { type IconProps, Skeleton, cn } from '@sr/ui-library';
import { format, parseISO } from 'date-fns';
import { removeTaskFromListsDuringRefreshes } from '../api/refreshAfterEmbeddedWorkflow';
import { useCheckUserPermissions } from '../hooks/useCheckUserPermissions';
import { WorkspaceTask } from '../types/taskTypes';
import {
  buildTaskEmbedPath,
  buildTaskEmbedWorkflowUrl,
  isMembershipGatedEmbed,
} from '../utils/embedWorkflow';
import { formatTaskSubtitle, getOriginalTitle } from '../utils/helpers/common';
import { DraftDeleteAction } from './DraftDeleteAction';
import { EntityCard } from './EntityCard';
import {
  EntityCardViewAction,
  VIEW_BUTTON_PROPS,
} from './EntityCardViewAction';
import { RoyaltiesRegistrationAction } from './RoyaltiesRegistrationAction';

/** Shown for a card field that has no value (APP-2209), instead of dropping the label. */
const MISSING_VALUE = '-';

/** Task-card dates, as the design shows them: "02 Feb 2026". */
export const formatTaskCardDate = (date?: null | string) =>
  date ? format(parseISO(date), 'dd MMM yyyy') : MISSING_VALUE;

const isSeriesWorkType = (workType?: string) =>
  (workType ?? '').toLowerCase().includes('series');

/**
 * Icon (and any extra classes) for a task card. Draft registrations reflect the
 * title's work type — series get the screen-and-play glyph, single works
 * (film / feature / documentary) get the vertical film strip. Other task types
 * keep their fixed icons.
 */
const getTaskCardIcon = (
  type: TaskTypeEnum,
  task: WorkspaceTask,
): { className?: string; icon: IconProps['icon'] } => {
  if (type === TaskTypeEnum.PotentialClaim) {
    return { icon: CurrencyCircleDollarIcon };
  }
  if (type === TaskTypeEnum.CompetingClaim) {
    return { icon: HandshakeIcon };
  }
  if (isSeriesWorkType(task?.details?.workType)) {
    return { icon: MonitorPlayIcon };
  }

  return { className: 'rotate-90', icon: FilmStripIcon };
};

/**
 * Background box size for a task card icon. Claims sit slightly smaller than the
 * title cards at 64×64; draft registrations use a poster ratio at 64×96 (w×h).
 */
const getTaskCardIconSize = (type: TaskTypeEnum) =>
  type === TaskTypeEnum.DraftRegistration ? 'h-24 w-16' : 'h-16 w-16';

const isCompetingClaim = (type: TaskTypeEnum) =>
  type === TaskTypeEnum.CompetingClaim;

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  currency: 'AUD',
  currencyDisplay: 'narrowSymbol',
  style: 'currency',
});

/**
 * A claim's estimated value, as MSR2's `Conflict.EstimatedValue` computes it:
 * paid plus unpaid, across the open years and the CCF year. A missing part
 * counts as 0; with every part missing there is no estimate. Potential claims
 * carry the same `valueSummary`, and their Est. Value sort uses this sum too.
 */
export const getTaskEstimatedValue = (task: WorkspaceTask) => {
  const parts = [
    task?.details?.valueSummary?.paidAllOpenYears,
    task?.details?.valueSummary?.paidCcfYear,
    task?.details?.valueSummary?.unpaidAllOpenYears,
    task?.details?.valueSummary?.unpaidCcfYear,
  ].filter((part): part is number => typeof part === 'number');

  return parts.length
    ? parts.reduce((total, part) => total + part, 0)
    : undefined;
};

/**
 * Drafts carry neither a status nor a due date, so their right-hand column holds
 * only the View button. Shared with `TaskCardSkeleton`, which has to reproduce
 * that column's height.
 */
const hasMetadata = (type: TaskTypeEnum) =>
  type !== TaskTypeEnum.DraftRegistration;

/** Only claims carry a `valueSummary`; other task types would show a blank forever. */
const hasEstimatedValue = (type: TaskTypeEnum) =>
  type === TaskTypeEnum.CompetingClaim || type === TaskTypeEnum.PotentialClaim;

/**
 * Below `xl`: a grid with metadata on its own row (actions `display: contents`).
 * From `xl`: one flex row where metadata never wraps and the title truncates.
 */
const CARD_LAYOUT = {
  actions: 'contents xl:flex xl:shrink-0 xl:flex-row xl:gap-4',
  buttons: 'flex items-center gap-2',
  content: 'min-w-0',
  metadata:
    'order-last col-span-2 grid grid-cols-[auto_auto_auto] justify-start gap-x-6 border-t sm:gap-x-8 border-border pt-3 xl:order-none xl:flex xl:gap-x-4 xl:border-0 xl:pt-0',
  /** `EntityCard.MetadataItem` right-aligns from `lg`; hold that until the row layout. */
  metadataItem: 'lg:items-start xl:items-end',
  /** Short values stay on one line so Status is the column that wraps. */
  noWrap: '[&>span]:whitespace-nowrap',
  root: 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 xl:flex',
};

/**
 * Title shown on a task card, mirroring each Home section:
 * competing claims use the claim title, everything else uses the original title.
 * Both are shown as sent: they are already cased, and re-casing mangles real
 * titles ("Anonymous" → "ANon-Ymous", "Star Wars: a New Hope").
 */
export const getTaskCardTitle = (task: WorkspaceTask, type: TaskTypeEnum) =>
  isCompetingClaim(type)
    ? (task?.details?.claimTitle ?? '').trim()
    : getOriginalTitle(task);

/**
 * Builds the embedded-workflow "View" URL for a task card. The resource id and
 * title name differ per type: competing claims key off `competingClaimId` /
 * `claimTitle`; potential claims and drafts key off `titleId` / original title.
 */
export const buildTaskCardViewUrl = (
  task: WorkspaceTask,
  type: TaskTypeEnum,
  currentSearch?: string,
) =>
  isCompetingClaim(type)
    ? buildTaskEmbedWorkflowUrl(
        type,
        task.competingClaimId,
        currentSearch,
        task?.details?.claimTitle,
      )
    : buildTaskEmbedWorkflowUrl(
        type,
        task.titleId,
        currentSearch,
        getOriginalTitle(task),
      );

export interface TaskCardProps {
  /** The task's own client; a competing claim shows it as the Claimant. */
  clientName?: string;
  /** Agent workspaces only: whether the card has a Claimant field at all. */
  hasClientField?: boolean;
  /** Agent workspaces resolve client names in a second request after the tasks. */
  isClientNamePending?: boolean;
  /** Competing claims only. */
  isOtherClaimantNamePending?: boolean;
  /** Draft cards only: called after the draft's title and task are both deleted. */
  onDeleted?: () => void;
  otherClaimantName?: string;
  task: WorkspaceTask;
  type: TaskTypeEnum;
  viewUrl?: string;
  workspaceId?: string;
}

/** A labelled subtitle line whose value may still be loading. */
const ClaimantLine = ({
  isPending,
  label,
  name,
}: {
  isPending: boolean;
  label: string;
  name?: string;
}) => (
  // Row wrapper, not a child of the `<p>`: the loading Skeleton is a `div`
  // and cannot legally nest inside `EntityCard.Subtitle`.
  <div className="flex min-w-0 items-center gap-1">
    <EntityCard.Subtitle className="mb-0 truncate">
      {label}: {!isPending && (name || MISSING_VALUE)}
    </EntityCard.Subtitle>

    {isPending && (
      <Skeleton aria-label={`Loading ${label}`} className="h-3 w-24" />
    )}
  </div>
);

/**
 * Stand-in for a `TaskCard` while its list loads. Built from the same
 * `EntityCard` shell and the same icon box, so its height is *produced* by the
 * real markup rather than a hand-computed number that can silently fall out of
 * step — a placeholder of the wrong height shifts everything below it the
 * moment the data lands.
 *
 * Both columns are reproduced, not just the left one: `EntityCard.Actions`
 * stacks below `sm`, where metadata plus the View button is taller than the
 * icon and therefore sets the card's height.
 */
export const TaskCardSkeleton = ({ type }: Pick<TaskCardProps, 'type'>) => (
  <EntityCard className={CARD_LAYOUT.root}>
    <EntityCard.Content className={CARD_LAYOUT.content}>
      <Skeleton className={cn(getTaskCardIconSize(type), 'rounded-lg')} />

      <EntityCard.Info>
        <Skeleton className="h-6 w-48" />

        {isCompetingClaim(type) ? (
          <>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-56" />
          </>
        ) : (
          <Skeleton className="mb-1.5 h-4 w-64" />
        )}
      </EntityCard.Info>
    </EntityCard.Content>

    <EntityCard.Actions className={CARD_LAYOUT.actions}>
      {/* Two `leading-4` lines, matching `EntityCard.MetadataItem`. */}
      {hasMetadata(type) && (
        <div className={CARD_LAYOUT.metadata}>
          <Skeleton className="h-8 w-16" />
        </div>
      )}

      <div className={CARD_LAYOUT.buttons}>
        {/* Matches `EntityCardViewAction`'s `size="sm"` button. */}
        <Skeleton className="h-8 w-[4.625rem]" />
      </div>
    </EntityCard.Actions>
  </EntityCard>
);

/**
 * Single task card shared by the Home task sections and the Tasks page list, so
 * the card markup lives in one place. Renders the per-type variant:
 * - PotentialClaim: subtitle (+ client name), Est. Value + Due Date
 * - CompetingClaim: Claimant (agents only) + Other Claimant, Status
 *   (taskDisplayStatus), Est. Value + Due Date
 * - DraftRegistration: subtitle (+ client name), no status, no due date
 *
 * A field the card type has is always labelled, showing "-" when its value is
 * missing, so every card of a type lines up and QA can read a blank as blank.
 */
export const TaskCard = ({
  clientName,
  hasClientField = false,
  isClientNamePending = false,
  isOtherClaimantNamePending = false,
  onDeleted,
  otherClaimantName,
  task,
  type,
  viewUrl,
  workspaceId,
}: TaskCardProps) => {
  const isCompeting = isCompetingClaim(type);
  const { className: iconClassName, icon } = getTaskCardIcon(type, task);
  const isDraft = type === TaskTypeEnum.DraftRegistration;
  const canDeleteDrafts = useCheckUserPermissions(
    [PermissionEnum.DeleteTitles],
    undefined,
    workspaceId,
  );

  const estimatedValue = getTaskEstimatedValue(task);

  return (
    <EntityCard className={CARD_LAYOUT.root}>
      <EntityCard.Content className={CARD_LAYOUT.content}>
        <EntityCard.Icon
          className={cn('w-16', iconClassName)}
          classes={{ wrapper: getTaskCardIconSize(type) }}
          icon={icon}
        />

        <EntityCard.Info className="min-w-0">
          <EntityCard.Title>{getTaskCardTitle(task, type)}</EntityCard.Title>

          {isCompeting ? (
            <div className="flex flex-col gap-0.5">
              {hasClientField && (
                <ClaimantLine
                  label="Claimant"
                  name={clientName}
                  isPending={isClientNamePending}
                />
              )}

              <ClaimantLine
                label="Other Claimant"
                name={otherClaimantName}
                isPending={isOtherClaimantNamePending}
              />
            </div>
          ) : (
            // Row wrapper, not a child of the `<p>`: the loading Skeleton is a
            // `div` and cannot legally nest inside `EntityCard.Subtitle`.
            <div className="flex min-w-0 items-center gap-1">
              <EntityCard.Subtitle>
                {formatTaskSubtitle(
                  task,
                  isClientNamePending ? undefined : clientName,
                )}
              </EntityCard.Subtitle>

              {isClientNamePending && (
                <Skeleton
                  aria-label="Loading client name"
                  className="mb-1.5 h-3 w-24"
                />
              )}
            </div>
          )}
        </EntityCard.Info>
      </EntityCard.Content>

      <EntityCard.Actions className={CARD_LAYOUT.actions}>
        {hasMetadata(type) && (
          <EntityCard.Metadata className={CARD_LAYOUT.metadata}>
            {isCompeting && (
              <EntityCard.MetadataItem
                label="Status"
                className={cn(
                  CARD_LAYOUT.metadataItem,
                  'xl:max-w-40 xl:text-right',
                )}
                // As sent, so the card reads exactly like the Status filter's options.
                value={task?.taskDisplayStatus || MISSING_VALUE}
              />
            )}

            {hasEstimatedValue(type) && (
              <EntityCard.MetadataItem
                label="Est. Value"
                className={cn(CARD_LAYOUT.metadataItem, CARD_LAYOUT.noWrap)}
                value={
                  estimatedValue === undefined
                    ? MISSING_VALUE
                    : currencyFormatter.format(estimatedValue)
                }
              />
            )}

            <EntityCard.MetadataItem
              label="Due Date"
              className={cn(CARD_LAYOUT.metadataItem, CARD_LAYOUT.noWrap)}
              value={formatTaskCardDate(task.dueDate)}
            />
          </EntityCard.Metadata>
        )}

        <div className={CARD_LAYOUT.buttons}>
          {isDraft && workspaceId && task.titleId && canDeleteDrafts && (
            <DraftDeleteAction
              titleId={task.titleId}
              onDeleteSuccess={() =>
                removeTaskFromListsDuringRefreshes(task._id)
              }
              onDeleted={onDeleted}
            />
          )}
          {isMembershipGatedEmbed(buildTaskEmbedPath(type, task.titleId)) ? (
            <RoyaltiesRegistrationAction
              label="View"
              buttonProps={VIEW_BUTTON_PROPS}
              to={viewUrl}
            />
          ) : (
            <EntityCardViewAction viewUrl={viewUrl} />
          )}
        </div>
      </EntityCard.Actions>
    </EntityCard>
  );
};
